import { useEffect, useState } from 'react';
import App from 'components/templates/app';
import Title from 'components/AuthTitle';
import Section from 'components/Section';
import Loader from 'components/SectionLoader';
import Button from 'components/Button';
import {
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import AdminMobileDeviceCard from 'components/cards/AdminMobileDeviceCard';
import AdminMobileDeviceTable from 'components/tables/AdminMobileDeviceTable';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from 'features/toastSlice';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { fetchAdminMobileDevices, fetchAllOrganizations } from 'api/portal';
import StatusSetupDialog from 'components/dialogs/StatusSetupDialog';
import Fade from 'components/Fade';
import styles from './styles.module.css';

const PAGE_SIZE = 8;

function AdminMobileDeviceList() {
  const [devices, setDevices] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterValue, setFilterValue] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('ANY');
  const [loadingError, setLoadingError] = useState(false);
  const [sortOrder, setSortOrder] = useState('NONE');
  const [viewMode, setViewMode] = useState('list');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const HOME = t('organization.breadcrumbs.home');
  const MANAGE_DEVICES = t('organization.breadcrumbs.manage-devices');
  const TITLE = t('organization.organization-mobile-devices-list.title-device-list');
  const LOAD_FAILURE = t('organization.organization-mobile-devices-list.loading-failure');

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('ANY');

  const breadcrumbs = [
    {
      path: '/',
      title: HOME,
    },
    {
      path: '/braincare/admin-mobile-devices',
      title: MANAGE_DEVICES,
      isActive: true,
    },
  ];

  const fetchDevices = (page, status = selectedStatus, text = filterValue, org = selectedOrganization) => {
    const searchParams = new URLSearchParams();

    searchParams.set('page', page);
    searchParams.set('page_size', PAGE_SIZE);

    const payload = {};

    if (status !== 'ANY') {
      payload.status = status;
    }

    if (text !== '') {
      payload.generic_text = text;
    }

    if (org !== 'ANY') {
      payload.organization_name_filter = org;
    }

    setLoadingError(false);
    setIsLoadingDevices(true);

    fetchAdminMobileDevices(searchParams, payload)
      .then((response) => {
        const { data } = response;
        let sortedDevices = data.results;

        if (sortOrder !== 'NONE') {
          sortedDevices = [...data.results].sort((a, b) => {
            const dateA = new Date(a.expiration);
            const dateB = new Date(b.expiration);
            return sortOrder === 'ASC' ? dateA - dateB : dateB - dateA;
          });
        }

        setIsLoadingDevices(false);
        setDevices(sortedDevices);
        setCount(Math.ceil(data.count / PAGE_SIZE));
        setCurrentPage(page);
        setLoadingError(false);
      })
      .catch(() => {
        setLoadingError(true);
        setIsLoadingDevices(false);
        dispatch(showErrorToast('organization.organization-mobile-devices-list.loading-failure'));
      });
  };

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams();
    searchParams.set('page_size', 9999);

    fetchAllOrganizations(searchParams)
      .then((response) => {
        setOrganizations(response.data.results.map((org) => org.name));
      })
      .catch(() => {
        setLoadingError(true);
        dispatch(showErrorToast('organization.organization-mobile-devices-list.loading-failure'));
      });
  }, []);

  useEffect(() => {
    fetchDevices(1);
  }, [sortOrder]);

  const handleClearFilterClick = () => {
    setFilterValue('');
    setSelectedStatus('ANY');
    setSelectedOrganization('ANY');
    fetchDevices(1, 'ANY', '', 'ANY');
  };

  const renderDevices = () => {
    if (loadingError) {
      return (
        <Typography marginTop="8px" variant="body2" color="error" data-testid="loading-error">
          {LOAD_FAILURE}
        </Typography>
      );
    }

    if (devices.length === 0) {
      return (
        <Typography marginTop="8px" variant="body2" color="secondary" data-testid="no-results">
          {t('organization.organization-devices.no-active-devices')}
        </Typography>
      );
    }

    if (viewMode === 'cards') {
      return (
        <Grid marginTop="8px" container spacing="12px">
          {devices.map((device) => (
            <Grid key={`device-card-${device.org_device_id}`} item xs={12} sm={6} md={3}>
              <AdminMobileDeviceCard
                device={device}
                sx={{ height: '100%' }}
                /* eslint-disable-next-line max-len */
                onClick={() => navigate(`/braincare/admin-mobile-devices/${device.organization_id}/${device.org_device_id}/info`)}
                data-testid={`device-card-${device.org_device_id}`}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    return (
      <AdminMobileDeviceTable
        devices={devices}
        /* eslint-disable-next-line max-len */
        onRowClick={(device) => navigate(`/braincare/admin-mobile-devices/${device.organization_id}/${device.org_device_id}/info`)}
        data-testid="devices-table"
      />
    );
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  return (
    <App breadcrumbs={breadcrumbs}>
      <Section className={styles.title}>
        <Title>{TITLE}</Title>
      </Section>

      <Section className={styles.container}>
        <form autoComplete="off" onSubmit={(event) => event.preventDefault()}>
          <Grid container spacing="12px" data-testid="mobile-devices-filters">
            <Grid item xs={12} sm={3}>
              <FormControl sx={{ margin: 0 }} fullWidth>
                <InputLabel htmlFor="status">
                  {t('organization.organization-mobile-devices-list.filters.trust-level')}
                </InputLabel>
                <Select
                  id="status"
                  onChange={(event) => setSelectedStatus(event.target.value)}
                  value={selectedStatus}
                  data-testid="select-status"
                  inputProps={{ 'data-testid': 'select-status-input' }}
                >
                  <MenuItem value="ANY">
                    {t('organization.organization-mobile-devices-list.filters.all-statuses')}
                  </MenuItem>
                  <MenuItem value="TRUSTED" data-testid="trusted-filter-option">
                    {t('organization.organization-mobile-devices-info.trusted')}
                  </MenuItem>
                  <MenuItem value="NORMAL" data-testid="normal-filter-option">
                    {t('organization.organization-mobile-devices-info.normal')}
                  </MenuItem>
                  <MenuItem value="BLOCKED" data-testid="blocked-filter-option">
                    {t('organization.organization-mobile-devices-info.blocked')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl sx={{ margin: 0 }} fullWidth>
                <InputLabel htmlFor="organization">
                  {t('organization.organization-mobile-devices-list.filters.organization')}
                </InputLabel>
                <Select
                  id="organization"
                  onChange={(event) => setSelectedOrganization(event.target.value)}
                  value={selectedOrganization}
                  data-testid="select-organization"
                  inputProps={{ 'data-testid': 'select-organization-input' }}
                >
                  <MenuItem value="ANY">
                    {t('organization.organization-mobile-devices-list.filters.all-organizations')}
                  </MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={`org-${org}`} value={org} data-testid={`org-filter-${org}`}>
                      {org}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl sx={{ margin: 0 }} fullWidth>
                <InputLabel htmlFor="filter">
                  {t('organization.organization-mobile-devices-list.filters.device')}
                </InputLabel>
                <OutlinedInput
                  name="search"
                  value={filterValue}
                  onChange={(event) => setFilterValue(event.target.value)}
                  placeholder={t('organization.organization-mobile-devices-list.filters.filter-device-placeholder')}
                  inputProps={{ 'data-testid': 'search-input' }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl sx={{ width: '250px' }}>
                <InputLabel htmlFor="sort-order">
                  {t('organization.organization-mobile-devices-list.sort-by-expiration')}
                </InputLabel>
                <Select
                  id="sort-order"
                  value={sortOrder}
                  onChange={handleSortChange}
                  label={t('organization.organization-mobile-devices-list.sort-by-expiration')}
                  data-testid="sort-order-select"
                >
                  <MenuItem value="NONE">{t('organization.organization-mobile-devices-list.sort-none')}</MenuItem>
                  <MenuItem value="ASC">{t('organization.organization-mobile-devices-list.sort-ascending')}</MenuItem>
                  <MenuItem value="DESC">{t('organization.organization-mobile-devices-list.sort-descending')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button variant="outlined" onClick={handleClearFilterClick} data-testid="reset-filters-button" fullWidth>
                {t('organization.organization-devices.button-filter-reset')}
              </Button>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                onClick={() => fetchDevices(1)}
                color="gradient"
                type="submit"
                data-testid="apply-filters-button"
                fullWidth
              >
                {t('organization.organization-devices.button-filter-apply')}
              </Button>
            </Grid>
          </Grid>
        </form>

        <Box display="flex" justifyContent="flex-end" marginTop="16px" marginBottom="16px">
          <Box>
            <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} aria-label="view mode">
              <ToggleButton value="cards" aria-label="card view">
                <ViewModuleIcon />
              </ToggleButton>
              <ToggleButton value="list" aria-label="list view">
                <ViewListIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Loader loading={isLoadingDevices}>
          <Fade>{renderDevices()}</Fade>
          <Pagination count={count} page={currentPage} onChange={(event, value) => fetchDevices(value)} />
        </Loader>
      </Section>
      <StatusSetupDialog
        isOpen={showDialog}
        onConfirm={(status) => navigate(`/braincare/admin-mobile-devices/add?status=${status}`)}
        onClose={() => setShowDialog(false)}
        data-testid="choose-status-dialog"
      />
    </App>
  );
}

export default AdminMobileDeviceList;
